import React from 'react';
import { format, parseISO, isValid } from 'date-fns';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

export interface MatchPlayer {
  id: string;
  name: string;
  nominated?: boolean;
}

export interface MatchData {
  playerNumber: number;
  homePlayer: MatchPlayer;
  awayPlayer: MatchPlayer;
  result: 'win' | 'loss' | 'half';
}

export interface FixtureResult {
  homeScore: number;
  awayScore: number;
  matches?: MatchData[];
  submittedBy?: string;
  submittedAt?: string;
}

export interface FixtureItemProps {
  id: string;
  match_date: string;
  tee_time?: string;
  status: 'scheduled' | 'completed' | 'cancelled';
  home_team: {
    id: string;
    name: string;
    division: {
      id: string;
      name: string;
    };
  };
  away_team: {
    id: string;
    name: string;
    division: {
      id: string;
      name: string;
    };
  };
  result?: FixtureResult;
  onClick?: () => void;
  highlightTeamName?: string;
  className?: string;
  actionButton?: React.ReactNode;
}

const FixtureItem: React.FC<FixtureItemProps> = ({
  match_date,
  tee_time,
  status,
  home_team,
  away_team,
  result,
  onClick,
  highlightTeamName,
  className,
  actionButton
}) => {
  const isMobile = useIsMobile();
  const isCompleted = status === 'completed';

  const formattedDate = format(parseISO(match_date), 'dd MMM yyyy');

  const formattedTime = React.useMemo(() => {
    try {
      if (!tee_time) return 'Time not set';
      const [hours, minutes] = tee_time.split(':');
      if (!hours || !minutes) return 'Time not set';
      const datePart = match_date.split('T')[0];
      const isoTime = `${datePart}T${hours}:${minutes}:00`;
      const parsed = parseISO(isoTime);
      return isValid(parsed) ? format(parsed, 'HH:mm') : 'Time not set';
    } catch {
      return 'Time not set';
    }
  }, [match_date, tee_time]);

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      e.preventDefault();
      onClick();
    }
  };

  const renderTeamName = (teamName: string, isHighlight: boolean) => (
    <span className="text-black">
      {teamName}
    </span>
  );

  const TeamNames = () => (
    <div className="flex items-center gap-2">
      {renderTeamName(home_team.name, home_team.name === highlightTeamName)}
      {isCompleted && result ? (
        <div className="mx-2 px-3 py-1 bg-muted rounded-md flex items-center">
          <span className="font-semibold">{result.homeScore}</span>
          <span className="mx-1">-</span>
          <span className="font-semibold">{result.awayScore}</span>
        </div>
      ) : (
        <div className="mx-2">vs</div>
      )}
      {renderTeamName(away_team.name, away_team.name === highlightTeamName)}
    </div>
  );

  return (
    <div
      className={cn('p-4', className, isCompleted && 'cursor-pointer')}
      onClick={isCompleted ? handleClick : undefined}
    >
      <div className="flex justify-between items-center">
        <div className="flex-1">
          <div className="text-sm mb-1">
            {formattedDate} • {formattedTime}
          </div>
          <TeamNames />
        </div>
        {!isCompleted && actionButton && <div>{actionButton}</div>}
      </div>
    </div>
  );
};

export default FixtureItem;